import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCompanySlug } from '../../../../client-experience/utils/companyAccess';
import { useStorefrontAuth } from '../../../auth/context/StorefrontAuthContext';
import { getStorefrontAppointmentDetailPath } from '../../utils/clientPortalPaths';
import { useStorefrontClientAppointments } from '../hooks/useStorefrontClientAppointments';
import { splitCurrentAndPastAppointments } from '../utils/appointmentDisplay';
import StorefrontAppointmentHistoryControls from '../components/StorefrontAppointmentHistoryControls';
import StorefrontAppointmentHistoryCard from '../components/StorefrontAppointmentHistoryCard';
import StorefrontAppointmentHistoryPagination from '../components/StorefrontAppointmentHistoryPagination';
import StorefrontAppointmentHistoryEmpty from '../components/StorefrontAppointmentHistoryEmpty';
import { CardStackSkeleton } from '../../../../components/loading/skeletons';
import '../styles/StorefrontAppointmentHistory.css';

const PER_PAGE = 10;

const StorefrontAppointmentHistoryPage = () => {
  const { storefrontClient, loading: authLoading, companyData } = useStorefrontAuth();
  const { companyUrl } = useParams();
  const companySlug = getCompanySlug(companyData) || companyUrl;

  const { appointments, loading, error } = useStorefrontClientAppointments(storefrontClient);

  const { currentAppointments, pastAppointments } = useMemo(
    () => splitCurrentAndPastAppointments(appointments),
    [appointments]
  );

  const [activeTab, setActiveTab] = useState('current');
  const [filters, setFilters] = useState({ search: '' });
  const [page, setPage] = useState(1);

  const tabList = activeTab === 'current' ? currentAppointments : pastAppointments;

  const filteredAppointments = useMemo(() => {
    let list = tabList;

    const q = filters.search.trim().toLowerCase();
    if (q) {
      list = list.filter((apt) => {
        const serviceName = (apt.serviceName || apt.service?.name || '').toLowerCase();
        const employeeName = (apt.employeeName || apt.employee?.name || '').toLowerCase();
        return serviceName.includes(q) || employeeName.includes(q);
      });
    }

    return list;
  }, [tabList, filters.search]);

  const totalPages = Math.max(1, Math.ceil(filteredAppointments.length / PER_PAGE));

  useEffect(() => {
    setPage(1);
  }, [filters, activeTab, appointments.length]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredAppointments.slice(start, start + PER_PAGE);
  }, [filteredAppointments, page]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (authLoading || loading) {
    return (
      <div className="sf-ah">
        <header className="sf-ah-header">
          <h1 className="sf-ah-title">Appointments</h1>
          <p className="sf-ah-subtitle">Upcoming and past visits for your account.</p>
        </header>
        <CardStackSkeleton cards={5} label="Loading appointments" />
      </div>
    );
  }

  return (
    <div className="sf-ah">
      <header className="sf-ah-header">
        <h1 className="sf-ah-title">Appointments</h1>
        <p className="sf-ah-subtitle">Upcoming and past visits for your account.</p>
      </header>

      {error ? (
        <div className="sf-ah-error" role="alert">
          {error}
        </div>
      ) : null}

      {!error && appointments.length === 0 ? (
        <StorefrontAppointmentHistoryEmpty companyUrl={companyUrl} />
      ) : null}

      {!error && appointments.length > 0 ? (
        <>
          <StorefrontAppointmentHistoryControls
            activeTab={activeTab}
            onTabChange={setActiveTab}
            currentCount={currentAppointments.length}
            pastCount={pastAppointments.length}
            filters={filters}
            onFilterChange={handleFilterChange}
          />

          {filteredAppointments.length === 0 ? (
            <p className="sf-ah-no-match">
              {tabList.length === 0
                ? (activeTab === 'current' ? 'No upcoming appointments.' : 'No past appointments yet.')
                : 'No appointments match your filters.'}
            </p>
          ) : (
            <ul className="sf-ah-list">
              {pageSlice.map((apt) => (
                <li key={apt.id}>
                  <StorefrontAppointmentHistoryCard
                    appointment={apt}
                    detailHref={getStorefrontAppointmentDetailPath(companySlug, apt)}
                  />
                </li>
              ))}
            </ul>
          )}

          <StorefrontAppointmentHistoryPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default StorefrontAppointmentHistoryPage;
